/**
 * GridTwinX Main Application Component
 *
 * PhD-level digital twin interface with:
 * - Geographic map visualization (TerriaJS/Leaflet)
 * - D3.js network schematic (one-line diagram)
 * - Real-time price charts (Recharts)
 * - Solver results analysis
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useStore } from './store';
import NZGridMap from './components/NZGridMap';
import ToolPanel from './components/ToolPanel';
import StatusBar from './components/StatusBar';
import LoadingScreen from './components/LoadingScreen';
import NetworkSchematicView from './components/NetworkSchematicView';
import DashboardView from './components/DashboardView';
import AnalysisInsightsPanel from './components/AnalysisInsightsPanel';
import { PriceChart } from './components/PriceChart';
import ScenariosView from './components/ScenariosView';
import FairnessMetricsPanel from './components/FairnessMetricsPanel';
import RunAuditPanel from './components/RunAuditPanel';
import AboutView from './components/AboutView';
import DataBadge from './components/DataBadge';
import AttributionPanel from './components/AttributionPanel';
import { API_BASE_URL } from './services/api';

// View types for tabbed interface
type ViewType = 'map' | 'dashboard' | 'schematic' | 'analysis' | 'scenarios' | 'about';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState<ViewType>('map');
  const [toolPanelOpen, setToolPanelOpen] = useState(true);
  const [toolPanelPinned, setToolPanelPinned] = useState(false);
  const { initializeApp, isConnected, solverResult, gridSummary } = useStore();

  useEffect(() => {
    const init = async () => {
      try {
        await initializeApp();
      } catch (error) {
        console.error('Failed to initialize app:', error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [initializeApp]);

  useEffect(() => {
    if (toolPanelPinned) return;
    setToolPanelOpen(activeView === 'map');
  }, [activeView, toolPanelPinned]);

  // Transform solver results to NetworkSchematic format
  const schematicData = useMemo(() => {
    if (!solverResult) {
      return {
        buses: [],
        branches: [],
        generators: [],
        loads: [],
      };
    }

    const branchLookup = new Map<number, { code: string; rate_mva: number }>();
    if (gridSummary?.branches) {
      gridSummary.branches.forEach((branch) => {
        const id = Number(branch.id);
        if (!Number.isNaN(id)) {
          branchLookup.set(id, { code: branch.code, rate_mva: branch.rate_mva });
        }
      });
    }

    const buses = solverResult.bus_results.map((bus, idx) => ({
      id: bus.bus_id,
      name: bus.name || bus.code || `Bus ${bus.bus_id}`,
      base_kv: 220, // Default, could be from case data
      bus_type: idx === 0 ? 'slack' : 'pq' as 'slack' | 'pv' | 'pq',
      vm_pu: bus.vm_pu,
      va_deg: bus.va_deg,
      p_mw: bus.p_mw,
      q_mvar: bus.q_mvar,
    }));

    const busNameLookup = new Map<number, string>();
    buses.forEach((bus) => busNameLookup.set(bus.id, bus.name));

    const branches = solverResult.branch_results.map((br) => {
      const summary = branchLookup.get(br.branch_id);
      const fromName = busNameLookup.get(br.from_bus) || `Bus ${br.from_bus}`;
      const toName = busNameLookup.get(br.to_bus) || `Bus ${br.to_bus}`;
      return {
        id: br.branch_id,
        from_bus: br.from_bus,
        to_bus: br.to_bus,
        name: summary?.code || `${fromName} -> ${toName}`,
        r_pu: 0.01,
        x_pu: 0.1,
        p_from_mw: br.p_mw,
        loading_percent: br.loading_percent,
        rate_mva: summary?.rate_mva,
      };
    });

    return {
      buses,
      branches,
      generators: [],
      loads: [],
    };
  }, [solverResult, gridSummary]);

  if (isLoading) {
    return <LoadingScreen message="Initializing GridTwinX..." />;
  }

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <h1 className="text-xl font-bold text-white">GridTwinX</h1>
          <span className="px-2 py-0.5 text-xs font-medium text-blue-300 bg-blue-900 rounded">
            Digital Twin
          </span>
        </div>

        {/* View Selector Tabs */}
        <div className="flex items-center gap-1 bg-gray-700 rounded-lg p-1">
          <ViewTab
            active={activeView === 'map'}
            onClick={() => setActiveView('map')}
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447 2.724A1 1 0 012 21.618V5.382a1 1 0 01.553-.894L9 1.764M9 20l6-3m-6 3V1.764M15 17l6 3V4l-6-3m0 16V1M15 17l-6-3" />
              </svg>
            }
            label="Geographic Map"
          />
          <ViewTab
            active={activeView === 'dashboard'}
            onClick={() => setActiveView('dashboard')}
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 12a1 1 0 011-1h6a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1v-7zM16 12a1 1 0 011-1h2a1 1 0 011 1v7a1 1 0 01-1 1h-2a1 1 0 01-1-1v-7z" />
              </svg>
            }
            label="Dashboard"
          />
          <ViewTab
            active={activeView === 'schematic'}
            onClick={() => setActiveView('schematic')}
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M17 7h.01M7 17h.01M17 17h.01M9 7h6M7 9v6m10-6v6M9 17h6" />
              </svg>
            }
            label="Network Schematic"
          />
          <ViewTab
            active={activeView === 'analysis'}
            onClick={() => setActiveView('analysis')}
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 19h16M6 16l3-4 3 3 4-6 2 3" />
              </svg>
            }
            label="Analysis"
          />
          <ViewTab
            active={activeView === 'scenarios'}
            onClick={() => setActiveView('scenarios')}
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 2h12M9 2v6l-4 7a4 4 0 003.5 6h7A4 4 0 0019 15l-4-7V2" />
              </svg>
            }
            label="EDGS Scenarios"
          />
          <ViewTab
            active={activeView === 'about'}
            onClick={() => setActiveView('about')}
            icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a6 6 0 110-12 6 6 0 010 12z" />
              </svg>
            }
            label="About"
          />
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              setToolPanelOpen((prev) => !prev);
              setToolPanelPinned(true);
            }}
            className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-xs font-medium text-gray-200"
            title="Toggle tool panel"
          >
            {toolPanelOpen ? 'Hide Tools' : 'Show Tools'}
          </button>
          <ConnectionStatus connected={isConnected} />
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Tool Panel (Left) */}
        {toolPanelOpen && (
          <aside className="w-80 bg-gray-800 border-r border-gray-700 overflow-y-auto">
            <ToolPanel />
          </aside>
        )}

        {/* Main Viewer (Center) */}
        <main className="flex-1 relative overflow-hidden">
          {activeView === 'dashboard' && (
            <div className="h-full w-full bg-gray-900 p-4 overflow-y-auto">
              <DashboardView variant="full" />
            </div>
          )}

          {activeView === 'map' && <NZGridMap />}

          {activeView === 'schematic' && (
            <div className="h-full w-full bg-gray-900 p-4 overflow-hidden">
              <NetworkSchematicView data={schematicData} />
            </div>
          )}

          {activeView === 'analysis' && (
            <div className="h-full w-full bg-gray-900 p-4 overflow-y-auto">
              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
                {/* Solver Results Summary */}
                <div className="bg-gray-800 rounded-lg p-4">
                  {solverResult ? (
                    <SolverResultsSummary result={solverResult} />
                  ) : (
                    <EmptyState
                      icon="🔬"
                      title="No Solver Results"
                      message="Run a power flow simulation to see detailed analysis."
                    />
                  )}
                </div>

                {/* Power Balance Chart */}
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg font-bold text-white">Power Balance</h3>
                    <DataBadge tier="MODELLED" />
                  </div>
                  {solverResult?.summary ? (
                    <PowerBalanceChart summary={solverResult.summary} />
                  ) : (
                    <EmptyState
                      icon="⚡"
                      title="No Power Data"
                      message="Run a simulation to view power balance."
                    />
                  )}
                </div>

                {/* Voltage Profile Bar Chart */}
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg font-bold text-white">Voltage Profile</h3>
                    <DataBadge tier="MODELLED" />
                  </div>
                  {solverResult ? (
                    <VoltageProfileChart buses={solverResult.bus_results} />
                  ) : (
                    <EmptyState
                      icon="📉"
                      title="No Voltage Data"
                      message="Run a simulation to view voltage profiles."
                    />
                  )}
                </div>

                {/* Branch Loading Bar Chart */}
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg font-bold text-white">Branch Loading</h3>
                    <DataBadge tier="MODELLED" />
                  </div>
                  {solverResult ? (
                    <BranchLoadingChart branches={solverResult.branch_results} />
                  ) : (
                    <EmptyState
                      icon="🔌"
                      title="No Branch Data"
                      message="Run a simulation to view branch loading."
                    />
                  )}
                </div>

                <div className="2xl:col-span-2">
                  <PriceChart height={320} autoRefresh={false} showStatistics={false} />
                </div>

                <FairnessMetricsPanel />

                <div className="2xl:col-span-2">
                  <RunAuditPanel />
                </div>

                <AttributionPanel />

                <AnalysisInsightsPanel />
              </div>
            </div>
          )}

          {activeView === 'scenarios' && (
            <div className="h-full w-full bg-gray-900 p-4 overflow-y-auto">
              <ScenariosView />
            </div>
          )}

          {activeView === 'about' && <AboutView />}
        </main>
      </div>

      {/* Status Bar */}
      <StatusBar />
    </div>
  );
};

// View tab component
const ViewTab: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'bg-blue-600 text-white'
        : 'text-gray-300 hover:text-white hover:bg-gray-600'
    }`}
  >
    <span className="text-gray-200">{icon}</span>
    <span className="hidden md:inline">{label}</span>
  </button>
);

// Connection status indicator
const ConnectionStatus: React.FC<{ connected: boolean }> = ({ connected }) => (
  <div className="flex items-center space-x-2" title={`API: ${API_BASE_URL}`}>
    <div
      className={`w-2 h-2 rounded-full ${
        connected ? 'bg-green-500' : 'bg-red-500'
      }`}
    />
    <span className="text-sm text-gray-400">
      {connected ? 'Connected' : 'Disconnected'}
    </span>
  </div>
);

// Empty state placeholder
const EmptyState: React.FC<{
  icon: string;
  title: string;
  message: string;
}> = ({ icon, title, message }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8">
    <div className="text-4xl mb-4">{icon}</div>
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 max-w-md">{message}</p>
  </div>
);

// Solver results summary component
const SolverResultsSummary: React.FC<{
  result: any;
}> = ({ result }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <h3 className="text-lg font-bold text-white">Solver Results</h3>
      <DataBadge tier="MODELLED" />
    </div>

    {/* Convergence status */}
    <div className={`text-2xl font-bold mb-4 ${result.converged ? 'text-green-400' : 'text-red-400'}`}>
      {result.converged ? '✓ Converged' : '✗ Did Not Converge'}
    </div>

    {/* Stats grid */}
    <div className="grid grid-cols-2 gap-4 mb-4">
      <StatCard label="Iterations" value={result.iterations} />
      <StatCard label="Solve Time" value={`${result.solve_time_ms?.toFixed(1) || 0} ms`} />
      <StatCard label="Max Mismatch" value={result.max_mismatch?.toExponential(2) || 'N/A'} />
      <StatCard label="Buses" value={result.bus_results?.length || 0} />
    </div>

    {/* Summary stats */}
    {result.summary && (
      <div className="border-t border-gray-700 pt-4">
        <h4 className="text-sm font-bold text-gray-400 mb-2">Power Balance</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Total Generation:</span>
            <span className="text-blue-400 font-mono">{result.summary.total_generation_mw?.toFixed(1) || 0} MW</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Total Load:</span>
            <span className="text-yellow-400 font-mono">{result.summary.total_load_mw?.toFixed(1) || 0} MW</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Total Losses:</span>
            <span className="text-red-400 font-mono">{result.summary.total_losses_mw?.toFixed(2) || 0} MW</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Voltage Range:</span>
            <span className="text-green-400 font-mono">
              {result.summary.min_voltage_pu?.toFixed(3) || 0} - {result.summary.max_voltage_pu?.toFixed(3) || 0} pu
            </span>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Stat card component
const StatCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="bg-gray-700 rounded p-3">
    <div className="text-xs text-gray-400">{label}</div>
    <div className="text-lg font-bold text-white">{value}</div>
  </div>
);

// Voltage profile bar chart
const VoltageProfileChart: React.FC<{
  buses: Array<{ name: string; code: string; vm_pu: number }>;
}> = ({ buses }) => {
  const sortedBuses = [...buses].sort((a, b) => a.vm_pu - b.vm_pu);
  const maxBars = 20;
  const displayBuses = sortedBuses.slice(0, maxBars);

  return (
    <div className="space-y-1">
      {displayBuses.map((bus, idx) => {
        const percentage = ((bus.vm_pu - 0.9) / 0.2) * 100; // 0.9-1.1 range
        const clampedPercentage = Math.max(0, Math.min(100, percentage));
        const isLow = bus.vm_pu < 0.95;
        const isHigh = bus.vm_pu > 1.05;

        return (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-16 text-xs text-gray-400 truncate" title={bus.name}>
              {bus.code || bus.name}
            </div>
            <div className="flex-1 h-4 bg-gray-700 rounded overflow-hidden">
              <div
                className={`h-full rounded ${
                  isLow ? 'bg-red-500' : isHigh ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${clampedPercentage}%` }}
              />
            </div>
            <div className={`w-16 text-xs font-mono text-right ${
              isLow ? 'text-red-400' : isHigh ? 'text-yellow-400' : 'text-green-400'
            }`}>
              {bus.vm_pu.toFixed(4)}
            </div>
          </div>
        );
      })}
      {buses.length > maxBars && (
        <div className="text-xs text-gray-500 text-center mt-2">
          Showing {maxBars} of {buses.length} buses (sorted by voltage)
        </div>
      )}
    </div>
  );
};

// Branch loading bar chart
const BranchLoadingChart: React.FC<{
  branches: Array<{ branch_id: number; loading_percent: number; p_mw: number }>;
}> = ({ branches }) => {
  const sortedBranches = [...branches].sort((a, b) => b.loading_percent - a.loading_percent);
  const maxBars = 15;
  const displayBranches = sortedBranches.slice(0, maxBars);

  return (
    <div className="space-y-1">
      {displayBranches.map((branch, idx) => {
        const loading = branch.loading_percent || 0;
        const isOverloaded = loading > 100;
        const isHighLoading = loading > 80;

        return (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-16 text-xs text-gray-400">
              Br {branch.branch_id}
            </div>
            <div className="flex-1 h-4 bg-gray-700 rounded overflow-hidden">
              <div
                className={`h-full rounded ${
                  isOverloaded ? 'bg-red-500' : isHighLoading ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(100, loading)}%` }}
              />
            </div>
            <div className={`w-20 text-xs font-mono text-right ${
              isOverloaded ? 'text-red-400' : isHighLoading ? 'text-yellow-400' : 'text-green-400'
            }`}>
              {loading.toFixed(1)}% ({branch.p_mw?.toFixed(0) || 0}MW)
            </div>
          </div>
        );
      })}
      {branches.length > maxBars && (
        <div className="text-xs text-gray-500 text-center mt-2">
          Showing {maxBars} of {branches.length} branches (sorted by loading)
        </div>
      )}
    </div>
  );
};

// Power balance visualization
const PowerBalanceChart: React.FC<{
  summary: {
    total_generation_mw: number;
    total_load_mw: number;
    total_losses_mw: number;
    min_voltage_pu: number;
    max_voltage_pu: number;
  };
}> = ({ summary }) => {
  const maxValue = Math.max(summary.total_generation_mw, summary.total_load_mw);

  const bars = [
    { label: 'Generation', value: summary.total_generation_mw, color: 'bg-blue-500', textColor: 'text-blue-400' },
    { label: 'Load', value: summary.total_load_mw, color: 'bg-yellow-500', textColor: 'text-yellow-400' },
    { label: 'Losses', value: summary.total_losses_mw, color: 'bg-red-500', textColor: 'text-red-400' },
  ];

  return (
    <div className="space-y-4">
      {bars.map((bar, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">{bar.label}</span>
            <span className={`font-mono ${bar.textColor}`}>{bar.value.toFixed(1)} MW</span>
          </div>
          <div className="h-6 bg-gray-700 rounded overflow-hidden">
            <div
              className={`h-full ${bar.color} rounded transition-all duration-500`}
              style={{ width: `${(bar.value / maxValue) * 100}%` }}
            />
          </div>
        </div>
      ))}

      {/* Loss percentage */}
      <div className="pt-2 border-t border-gray-700">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Loss Percentage</span>
          <span className="text-red-400 font-mono">
            {((summary.total_losses_mw / summary.total_generation_mw) * 100).toFixed(2)}%
          </span>
        </div>
      </div>

      {/* Voltage range */}
      <div className="pt-2 border-t border-gray-700">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Voltage Range</span>
          <span className="text-green-400 font-mono">
            {summary.min_voltage_pu.toFixed(4)} - {summary.max_voltage_pu.toFixed(4)} pu
          </span>
        </div>
        <div className="mt-2 h-4 bg-gray-700 rounded relative">
          {/* Normal range indicator */}
          <div
            className="absolute h-full bg-green-600/30 rounded"
            style={{ left: '25%', width: '50%' }}
          />
          {/* Min marker */}
          <div
            className="absolute h-full w-1 bg-yellow-500 rounded"
            style={{ left: `${((summary.min_voltage_pu - 0.9) / 0.2) * 100}%` }}
            title={`Min: ${summary.min_voltage_pu.toFixed(4)} pu`}
          />
          {/* Max marker */}
          <div
            className="absolute h-full w-1 bg-yellow-500 rounded"
            style={{ left: `${((summary.max_voltage_pu - 0.9) / 0.2) * 100}%` }}
            title={`Max: ${summary.max_voltage_pu.toFixed(4)} pu`}
          />
          {/* Labels */}
          <div className="absolute -bottom-4 left-0 text-xs text-gray-500">0.9</div>
          <div className="absolute -bottom-4 right-0 text-xs text-gray-500">1.1</div>
        </div>
      </div>
    </div>
  );
};

export default App;
